/**
 * RemovePromoterModal.tsx - Promoter Removal Confirmation Modal
 *
 * This component provides a confirmation interface for removing promoters
 * from fundraisers with appropriate warnings about data preservation.
 *
 * Key Features:
 * - Promoter information display
 * - Removal confirmation with warnings
 * - Data preservation options
 * - Impact assessment (donations, performance)
 * - Secure removal process
 *
 * Removal Process:
 * - Display promoter details and performance
 * - Show impact of removal (donations will remain)
 * - Confirm removal with explicit consent
 * - Process removal while preserving donation history
 *
 * Props:
 * - isVisible: boolean - Modal visibility state
 * - onClose: () => void - Close modal handler
 * - promoter: Promoter | null - Promoter to remove
 * - onConfirm: () => void - Removal confirmation handler
 *
 * Dependencies: Promoter types, portal rendering
 */

import React from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { X } from "lucide-react";
import { IPromoter } from "../interfaces";

const Modal = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 999999;
  backdrop-filter: blur(4px);
  padding: 20px;
  overflow-y: auto;
  box-sizing: border-box;
`;

const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 24px;
  padding: 32px;
  width: 90%;
  max-width: 450px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  text-align: center;
  flex: 1;
`;

const CloseButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: #e2e8f0;
  }
`;

const ModalText = styled.p`
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 24px;
  font-size: 16px;
  text-align: left;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 40px;
`;

const ActionButton = styled.button<{ variant?: "active" | "inactive" }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  ${(props) => {
    switch (props.variant) {
      case "active":
        return `
          background: #dcfce7;
          color: #166534;
          
          &:hover {
            background: #bbf7d0;
          }
        `;
      case "inactive":
        return `
          background: #fef3c7;
          color: #92400e;
          
          &:hover {
            background: #fde68a;
          }
        `;
      default:
        return `
          background: #f1f5f9;
          color: #64748b;
          
          &:hover {
            background: #e2e8f0;
          }
        `;
    }
  }}
`;

interface RemovePromoterModalProps {
  isVisible: boolean;
  promoter: IPromoter | null;
  onClose: () => void;
  onMakeInactive: (isActive:boolean) => void;
  formatCurrency: (amount: number) => string;
  getRemovalTotal: (promoterId: string) => number;
}

export const RemovePromoterModal: React.FC<RemovePromoterModalProps> = ({
  isVisible,
  promoter,
  onClose,
  onMakeInactive,
  formatCurrency,
  getRemovalTotal,
}) => {
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  React.useEffect(() => {
    if (isVisible) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [isVisible]);

  if (!promoter) return null;

  const removalTotal = getRemovalTotal(promoter.id);

  return createPortal(
    <Modal visible={isVisible} onClick={handleOverlayClick}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Remove Promoter</ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalText>
          <strong>{promoter.displayName}</strong> is responsible for{" "}
          <strong>{formatCurrency(removalTotal)}</strong> in donations and
          cannot be removed from the fundraiser.
          <br />
          <br />
          {!promoter.isActive
            ? "The promoter is currently inactive. Do you want to make active instead?"
            : "The promoter is currently active. Do you want to make inactive instead?"}
        </ModalText>

        <ModalActions>
          {!promoter.isActive ? (
            <>
              <ActionButton variant="active" onClick={()=>onMakeInactive(true)}>
                Make Active
              </ActionButton>
              <ActionButton variant="inactive" onClick={onClose}>
                Keep Inactive
              </ActionButton>
            </>
          ) : (
            <>
              <ActionButton variant="active" onClick={onClose}>
                Keep Active
              </ActionButton>
              <ActionButton variant="inactive" onClick={()=>onMakeInactive(false)}>
                Make Inactive
              </ActionButton>
            </>
          )}
        </ModalActions>
      </ModalContent>
    </Modal>,
    document.body
  );
};
